import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import navigator from 'pared/Routes/navigator'
import { track } from 'pared/analytics/team'
import EmployeeFeedbackSpan from 'pared/components/managerReview/EmployeeFeedbackSpan'
import ManagerReview from 'pared/components/managerReview/ManagerReview'
import { DEFAULT_EMPLOYEE_STATUS } from 'pared/constants'
import COLORS from 'pared/constants/colors'
import { IPeriod } from 'pared/data/getPeriods'

import useTeamConfig from '../hooks/useTeamConfig'
import EmployeeStatusEditButton from './EmployeeStatusEditButton'
import { ILocation, ITeamMember } from './index'

interface IProps {
  userEmployeeId: number | null
  allPeriods: IPeriod[]
  location: ILocation | null
  teamMembers: ITeamMember[]
  onRefreshTable: () => Promise<void>
  shouldShowFeedback: boolean
}

function Main({
  userEmployeeId,
  allPeriods,
  location,
  teamMembers,
  onRefreshTable,
  shouldShowFeedback,
}: IProps) {
  const teamConfig = useTeamConfig()
  // State to track how many feedbacks to show for each employee
  const [visibleFeedbacksMap, setVisibleFeedbacksMap] = useState<{
    [key: number]: number
  }>({})

  let feedbackTableContent = null

  if (!location) {
    feedbackTableContent = (
      <tr>
        <td colSpan={5}>Loading ...</td>
      </tr>
    )
  } else if (teamMembers.length > 0) {
    const uniqueMemberKeySet = new Set<string>()
    feedbackTableContent = teamMembers.reduce((result, member) => {
      const memberKey = `${member.roleName}-${member.firstName}-${member.lastName}-${member.employmentType}-${member.tenure}`
      if (uniqueMemberKeySet.has(memberKey)) {
        return result
      }
      uniqueMemberKeySet.add(memberKey)

      // Get number of visible feedbacks for this employee (default to 3)
      const visibleCount = visibleFeedbacksMap[member.employeeId] || 3
      const totalReviews = member.managerReviews.length
      const visibleReviews = member.managerReviews.slice(0, visibleCount)
      const hasMoreReviews = totalReviews > visibleCount

      const handleShowMore = () => {
        setVisibleFeedbacksMap((prev) => ({
          ...prev,
          [member.employeeId]: (prev[member.employeeId] || 3) + 3,
        }))
      }

      const managerReviewDetails = (
        <>
          {visibleReviews.map((review) => (
            <ManagerReview
              key={review.id}
              performanceName={review.performanceName}
              feedback={review.feedback}
              reviewer={review.reviewer}
              formattedReviewedAt={review.formattedReviewedAt}
              reviewedAt={review.reviewedAt}
              managerReviewId={review.id}
              allPeriods={allPeriods}
              firstName={member.firstName}
              lastName={member.lastName}
              role={member.roleName}
              storeCode={location.code}
              storeName={location.name}
              onRefresh={onRefreshTable}
            />
          ))}
          {hasMoreReviews && (
            <ShowMoreContainer>
              <ShowMoreLink onClick={handleShowMore}>
                Click to see more... ({totalReviews - visibleCount} more)
              </ShowMoreLink>
            </ShowMoreContainer>
          )}
        </>
      )

      const employeeFeedbackSpan = userEmployeeId ? (
        <EmployeeFeedbackSpan
          firstName={member.firstName}
          lastName={member.lastName}
          role={member.roleName}
          storeCode={location.code}
          storeName={location.name}
          revieweeId={member.employeeId}
          locationId={location.id}
          onRefresh={onRefreshTable}
        />
      ) : null

      const feedbackSectionBody = shouldShowFeedback ? (
        <>
          <td>
            <div>
              {member.status || DEFAULT_EMPLOYEE_STATUS.name}
              {userEmployeeId ? (
                <StatusEditSpan>
                  <EmployeeStatusEditButton
                    employeeId={member.employeeId}
                    employeeName={`${member.firstName} ${member.lastName}`}
                    locationId={member.locationId}
                    roleId={member.roleId}
                    editedByEmployeeId={userEmployeeId}
                    currentEmployeeStatus={member.status}
                    onRefresh={onRefreshTable}
                  />
                </StatusEditSpan>
              ) : null}
            </div>
            {member.statusRecordedBy ? (
              <StatusEditedByDiv>
                <div>
                  Edited by{' '}
                  <Link to={navigator.employeeProfile(member.statusRecordedBy)}>
                    {member.statusRecordedByName}
                  </Link>
                </div>
                <div>on {member.statusRecordedAt}</div>
              </StatusEditedByDiv>
            ) : null}
          </td>
          <RecentFeedbackTd>
            {totalReviews > 0 ? managerReviewDetails : <>-</>}
          </RecentFeedbackTd>
          <BaseTd>{employeeFeedbackSpan}</BaseTd>
        </>
      ) : null

      const trackEventManagerProfileLinkClicked = () => {
        track.managerProfileLinkClicked(member.employeeId)
      }

      // Format tenure and time in position for two-line display
      const formatDuration = (duration: string) => {
        if (duration === '-') {
          return {
            years: '-',
            months: '',
          }
        }

        if (duration === 'less than 1 month') {
          return {
            years: '',
            months: 'less than 1 month',
          }
        }

        const parts = duration.split(' ')
        let years = ''
        let months = ''

        for (let i = 0; i < parts.length; i++) {
          if (parts[i].includes('year')) {
            years = parts[i - 1] + ' ' + parts[i]
          } else if (parts[i].includes('month')) {
            months = parts[i - 1] + ' ' + parts[i]
          }
        }

        // Return empty string instead of '-' when there's no value
        return { years: years, months: months }
      }

      const tenureFormatted = formatDuration(member.tenure)
      const timeInPositionFormatted = formatDuration(member.timeInPosition)

      return [
        ...result,
        <tr key={member.employeeId}>
          <TeamMemberTd>
            <TeamMemberRole>{member.roleName}</TeamMemberRole>
            <TeamMemberName>
              {teamConfig.hasLinkToViewEmployeeProfile ? (
                <Link
                  to={navigator.employeeProfile(member.employeeId)}
                  onClick={trackEventManagerProfileLinkClicked}
                >
                  {member.firstName} {member.lastName}
                </Link>
              ) : (
                `${member.firstName} ${member.lastName}`
              )}
            </TeamMemberName>
            <TeamMemberEmployment>{member.employmentType}</TeamMemberEmployment>
          </TeamMemberTd>
          <DurationTd>
            {tenureFormatted.years && (
              <DurationYears>{tenureFormatted.years}</DurationYears>
            )}
            {tenureFormatted.months && (
              <DurationMonths>{tenureFormatted.months}</DurationMonths>
            )}
          </DurationTd>
          {teamConfig.shouldHideTimeInPosition ? null : (
            <DurationTd>
              {timeInPositionFormatted.years && (
                <DurationYears>{timeInPositionFormatted.years}</DurationYears>
              )}
              {timeInPositionFormatted.months && (
                <DurationMonths>
                  {timeInPositionFormatted.months}
                </DurationMonths>
              )}
            </DurationTd>
          )}
          {feedbackSectionBody}
        </tr>,
      ]
    }, [] as JSX.Element[])
  } else {
    feedbackTableContent = (
      <tr>
        <td colSpan={5}>No team member found.</td>
      </tr>
    )
  }

  const feedbackSectionHeader = shouldShowFeedback ? (
    <>
      <th>Status</th>
      <FeedbackTh>Feedback</FeedbackTh>
    </>
  ) : null

  return (
    <FeedbackTable>
      <thead>
        <tr>
          <th>Team Member</th>
          <th>Tenure</th>
          {teamConfig.shouldHideTimeInPosition ? null : (
            <th>Time in Position</th>
          )}
          {feedbackSectionHeader}
        </tr>
      </thead>
      <tbody>{feedbackTableContent}</tbody>
    </FeedbackTable>
  )
}

const FeedbackTable = styled.table`
  width: 100%;
  min-width: 1000px;
  max-width: 1200px;
  border-collapse: collapse;
  text-align: left;

  thead tr {
    border-bottom: 1px solid ${COLORS.Chalkboard};
  }

  tbody tr:nth-child(even) {
    background-color: ${COLORS.Porcelain};
  }

  th {
    font-family: Lexend-SemiBold;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    padding: 10px 0;
  }

  td {
    font-family: Lexend-Regular;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    padding: 15px 0;
    vertical-align: top;
  }
`

const StatusEditSpan = styled.span`
  font-family: Lexend-Regular;
  color: #07a4c7;
  text-decoration: underline;
  margin: 0 0 0 15px;
`

const StatusEditedByDiv = styled.div`
  font-family: Lexend-Regular;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  padding: 20px 0 15px 0;
`

const BaseTd = styled.td`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`

const RecentFeedbackTd = styled(BaseTd)`
  min-width: 600px;
  width: 40%;
`

const ShowMoreContainer = styled.div`
  margin-top: 10px;
`

const ShowMoreLink = styled.a`
  color: #07a4c7;
  text-decoration: underline;
  cursor: pointer;
  font-family: Lexend-Regular;
  font-size: 12px;

  &:hover {
    text-decoration: none;
  }
`

const TeamMemberTd = styled.td`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  padding: 15px 0;
  vertical-align: top;
`

const TeamMemberRole = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 4px;
`

const TeamMemberName = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 4px;
`

const TeamMemberEmployment = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-weight: 400;
`

const FeedbackTh = styled.th`
  min-width: 600px;
  width: 40%;
`

const DurationTd = styled.td`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  padding: 15px 0;
  vertical-align: top;
`

const DurationYears = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 4px;
`

const DurationMonths = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-weight: 400;
`

export default Main
